import React, { useState, useEffect, useCallback } from 'react';
import './EvercontactUsers.css';
import UserDetail from './UserDetail';

const EvercontactUsers = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Filter state
  const [activeOnly, setActiveOnly] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 100;

  // Debounce search input to avoid a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeOnly]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(usersPerPage),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (activeOnly) params.set('activeOnly', 'true');

      const response = await fetch(`/api/users?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Support both new paginated shape and legacy array-only shape
      if (Array.isArray(data)) {
        setUsers(data);
        setTotalUsers(data.length);
      } else {
        setUsers(data.users || []);
        setTotalUsers(typeof data.total === 'number' ? data.total : (data.users || []).length);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, activeOnly]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const isUserActive = (user) => {
    return user.status === 1 || user.status === '1' || user.status === 'active';
  };

  // Server handles filtering + pagination now
  const totalPages = Math.max(1, Math.ceil(totalUsers / usersPerPage));
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = users;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
  };

  const handleBackToList = () => {
    setSelectedUserId(null);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // If a user is selected, show the UserDetail component
  if (selectedUserId) {
    return <UserDetail userId={selectedUserId} onBack={handleBackToList} />;
  }

  if (loading) {
    return (
      <div className="users-container">
        <div className="users-header">
          <h2>Evercontact Users</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-container">
        <div className="users-header">
          <h2>Evercontact Users</h2>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchUsers} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Evercontact Users</h2>
        <div className="users-stats">
          <span className="users-count">{totalUsers} users</span>
          {totalPages > 1 && (
            <span className="page-info">Page {currentPage} of {totalPages}</span>
          )}
        </div>
      </div>

      <div className="search-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users by email, name, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="search-icon">🔍</div>
        </div>
        <label className="active-filter">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active only (status = 1)
        </label>
      </div>

      <div className="users-list">
        {paginatedUsers.length === 0 ? (
          <div className="no-users">
            <p>No users found</p>
          </div>
        ) : (
          <div className="users-table">
            <div className="users-table-header">
              <div className="header-cell header-user">User</div>
              <div className="header-cell header-email">Email</div>
              <div className="header-cell header-joined">Joined</div>
              <div className="header-cell header-status">Status</div>
            </div>
            {paginatedUsers.map((user, index) => (
              <div 
                key={user._id || index} 
                className={`user-row ${isUserActive(user) ? '' : 'inactive-row'}`} 
                onClick={() => handleUserClick(user._id)}
              >
                <div className="user-cell user-info-cell">
                  <div className="user-avatar-small">
                    <span>{user.email ? user.email.substring(0, 2).toUpperCase() : 'U'}</span>
                  </div>
                  <div className="user-basic-info">
                    <span className="user-name-list">{user.name || 'No name'}</span>
                    <span className="user-id-list">{user._id || 'No ID'}</span>
                  </div>
                </div>
                <div className="user-cell user-email-cell">
                  <span className="user-email-list">{user.email || 'No email'}</span>
                </div>
                <div className="user-cell user-joined-cell">
                  <span className="user-date-list">
                    {user.createdAt 
                      ? new Date(user.createdAt).toLocaleDateString() 
                      : 'Unknown'}
                  </span>
                </div>
                <div className="user-cell user-status-cell">
                  <span className={`status-badge ${user.status || 'active'}`}>
                    {user.status || 'active'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          
          <div className="pagination-numbers">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )
            ))}
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
      
      {totalPages > 1 && (
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(startIndex + usersPerPage, totalUsers)} of {totalUsers} users
        </div>
      )}
    </div>
  );
};

export default EvercontactUsers;
